import { useId, useRef } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useFocusTrap } from './useFocusTrap';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  title: string;
  message: string;
  /** Label of the affirmative (usually destructive) action button. */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * The app's styled confirm step (cue-forge-drt.19), replacing `window.confirm`
 * for destructive discards so the prompt is theme-aware and focus-managed like
 * every other modal. Small and dependency-free on purpose: it is imported by the
 * eager session/convert paths, so it must never drag a lazy chunk along.
 *
 * Behaviour mirrors the app's dialog conventions (ShortcutsHelp et al.): focus
 * lands on the safe (cancel) action, Tab is trapped, Escape and a backdrop click
 * cancel, and focus returns to the opener on close. It can also sit on top of
 * another dialog (the Advanced editor / preset editor discard guards), so its
 * key and click events never leak to the parent dialog's own close handlers.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const messageId = useId();

  // Focus the safe (cancel) action on open, restore focus to the opener on
  // close, and trap Tab within the panel (shared with the app's other
  // dialogs — see `useFocusTrap`).
  const { onKeyDown: trapTab } = useFocusTrap(panelRef, { initialFocusRef: cancelRef });

  function onKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    // Never let Escape/Tab bubble to a parent dialog's handlers — that would
    // close the dialog whose work this confirm is protecting.
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key !== 'Tab') return;
    e.stopPropagation();
    trapTab(e);
  }

  return (
    <div
      className="cf-modal cf-confirm"
      onClick={(e) => {
        e.stopPropagation(); // a parent dialog's backdrop handler must not also fire
        onCancel();
      }}
    >
      <div
        ref={panelRef}
        className="cf-modal__panel cf-confirm__panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={messageId}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cf-confirm__body">
          <h2 id={titleId} className="cf-confirm__title">
            {title}
          </h2>
          <p id={messageId} className="cf-confirm__message">
            {message}
          </p>
          <div className="cf-confirm__actions">
            <button ref={cancelRef} type="button" className="cf-btn" onClick={onCancel}>
              {cancelLabel}
            </button>
            <button type="button" className="cf-btn cf-btn--primary" onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
